/**
 * POI preferences state hook using RQ cache bridge + localStorage persistence.
 *
 * Same dual-write pattern as useCommutePreferences.
 */

import { useQuery, useQueryClient } from '@zg-rentals/react-query5';
import { useCallback, useEffect } from 'react';
import { neighborhoodStateKeys, type PoiPreferences } from './neighborhoodStateKeys';

const STORAGE_KEY = 'hp-poi-preferences';

const DEFAULT_PREFERENCES: PoiPreferences = {
  enabledCategories: [],
  hasCustomSelection: false,
};

function normalizePreferences(preferences: unknown): PoiPreferences {
  if (!preferences || typeof preferences !== 'object') return DEFAULT_PREFERENCES;

  const enabledCategories =
    'enabledCategories' in preferences && Array.isArray(preferences.enabledCategories)
      ? preferences.enabledCategories.filter((category): category is string => typeof category === 'string')
      : [];

  const hasCustomSelection =
    'hasCustomSelection' in preferences && typeof preferences.hasCustomSelection === 'boolean'
      ? preferences.hasCustomSelection
      : enabledCategories.length > 0;

  return {
    enabledCategories,
    hasCustomSelection,
  };
}

function readFromStorage(): PoiPreferences {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? normalizePreferences(JSON.parse(stored)) : DEFAULT_PREFERENCES;
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

/**
 * Writes POI preferences to RQ cache and localStorage.
 */
export function useSetPoiPreferences() {
  const queryClient = useQueryClient();

  return useCallback(
    (preferences: PoiPreferences) => {
      const normalizedPreferences = normalizePreferences(preferences);
      queryClient.setQueryData<PoiPreferences>(
        neighborhoodStateKeys.poiPreferences(),
        normalizedPreferences,
      );
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedPreferences));
        } catch {
          // localStorage unavailable
        }
      }
    },
    [queryClient],
  );
}

/**
 * Reads POI preferences from RQ cache. Initializes from localStorage on first read.
 */
export function usePoiPreferences(): PoiPreferences {
  const queryClient = useQueryClient();
  const setPreferences = useSetPoiPreferences();

  useEffect(() => {
    const existing = queryClient.getQueryData<PoiPreferences>(
      neighborhoodStateKeys.poiPreferences(),
    );
    if (!existing) {
      setPreferences(readFromStorage());
    }
  }, [queryClient, setPreferences]);

  const { data } = useQuery<PoiPreferences>({
    queryKey: neighborhoodStateKeys.poiPreferences(),
    queryFn: () => {
      return (
        queryClient.getQueryData<PoiPreferences>(
          neighborhoodStateKeys.poiPreferences(),
        ) ?? readFromStorage()
      );
    },
    initialData: () =>
      queryClient.getQueryData<PoiPreferences>(neighborhoodStateKeys.poiPreferences()) ??
      readFromStorage(),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return data ?? DEFAULT_PREFERENCES;
}
